import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { ArrowLeft, Download, Calendar, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useBranch } from '../contexts/BranchContext';
import { useAuth } from '../contexts/AuthContext';
import { misReportApiService, MISDailyReportDto } from '../services/misReportApi';

interface MISReportData {
  metric: string;
  days: { [dateKey: string]: number | string };
}

const MIS_METRICS = [
  'RM Stock As on dated (end of month)',
  'Consumption of RM (monthly)',
  'No. of Final parts produced in Blanking Section',
  'No. of parts rejected in Blanking Section',
  'No. of Retainer produced in Auto Section',
  'No. of Retainer rejected in Auto Section',
  'No. of Parts produced In Welding + Tapping section',
  'No. of parts rejected in Welding And Tapping',
  'No. of parts Produced in a retainer Section (TRB+Forming+Facing)',
  'No. of parts rejected at Retainer Line'
];

const MISReportPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<MISReportData[]>([]);
  const [dateKeys, setDateKeys] = useState<string[]>([]);

  // Date range state - default to start of current month to today
  const now = new Date();
  const getToday = () => {
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getStartOfMonth = () => {
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-01`;
  };

  const [startDate, setStartDate] = useState<string>(getStartOfMonth());
  const [endDate, setEndDate] = useState<string>(getToday());

  // Generate date keys for the date range
  const generateDateKeys = (start: string, end: string): string[] => {
    const dates: string[] = [];
    const startDateObj = new Date(start);
    const endDateObj = new Date(end);
    
    const currentDate = new Date(startDateObj);
    while (currentDate <= endDateObj) {
      const dateKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
      dates.push(dateKey);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    // Reverse to show most recent dates first
    return dates.reverse();
  };

  const formatDateLabel = (dateKey: string): string => {
    const [year, month, day] = dateKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const loadReportData = async () => {
    const branchId = selectedBranch || user?.defaultBranchId;
    
    if (!branchId) {
      toast.error('Please select a branch to view the MIS daily report');
      return;
    }

    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Start date cannot be after end date');
      return;
    }
    
    setLoading(true);
    try {
      const response = await misReportApiService.getMISDailySummaryByBranchAndDateRange(
        branchId,
        startDate,
        endDate
      );
      
      if (response.success && response.data) {
        // Extract date keys from API response (use dayKey or reportDate)
        const keysFromApi = response.data
          .map(item => item.dayKey || item.reportDate)
          .filter((key): key is string => !!key)
          .sort((a, b) => b.localeCompare(a)); // Sort descending (most recent first)
        
        setDateKeys(keysFromApi);

        // Transform daily data from API response
        const transformedData = transformDailyData(response.data, keysFromApi);
        setReportData(transformedData);
      } else {
        toast.error(response.message || 'Failed to load MIS daily report');
        setReportData([]);
      }
    } catch (error) {
      // console.error('Error loading MIS daily report:', error);
      toast.error('Failed to load MIS daily report');
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  // Load report on component mount and when branch changes (but not when dates change)
  useEffect(() => {
    const branchId = selectedBranch || user?.defaultBranchId;
    if (branchId && startDate && endDate) {
      loadReportData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch, user?.defaultBranchId]); // Only depend on branch - dates are handled by Load Report button

  // Transform daily data from API response
  const transformDailyData = (apiData: MISDailyReportDto[], dateKeys: string[]): MISReportData[] => {
    // Initialize result structure with all metrics
    const result: MISReportData[] = MIS_METRICS.map(metric => {
      const days: { [key: string]: number } = {};
      dateKeys.forEach(dateKey => {
        days[dateKey] = 0;
      });
      return { metric, days };
    });

    // Metric mapping: metric name -> API field name
    const metricMapping = [
      { metric: 'RM Stock As on dated (end of month)', field: 'rmStockQtyAsOnDate' },
      { metric: 'Consumption of RM (monthly)', field: 'rmConsumptionQty' },
      { metric: 'No. of Final parts produced in Blanking Section', field: 'blankingPartsProduced' },
      { metric: 'No. of parts rejected in Blanking Section', field: 'blankingPartsRejected' },
      { metric: 'No. of Retainer produced in Auto Section', field: 'autoRetainerPartsProduced' },
      { metric: 'No. of Retainer rejected in Auto Section', field: 'autoRetainerPartsRejected' },
      { metric: 'No. of Parts produced In Welding + Tapping section', field: 'weldingTappingPartsProduced' },
      { metric: 'No. of parts rejected in Welding And Tapping', field: 'weldingTappingPartsRejected' },
      { metric: 'No. of parts Produced in a retainer Section (TRB+Forming+Facing)', field: 'retainerPartsProduced' },
      { metric: 'No. of parts rejected at Retainer Line', field: 'retainerPartsRejected' },
    ];

    // Populate days data from API response
    apiData.forEach((dailyData) => {
      // Get date key from dayKey or reportDate field
      const dateKey = dailyData.dayKey || dailyData.reportDate || '';
      
      if (!dateKey) return;

      // Update each metric for this date
      metricMapping.forEach(({ metric, field }, index) => {
        const value = (dailyData as any)[field];
        if (value !== null && value !== undefined) {
          // Initialize the date key if it doesn't exist
          if (!result[index].days.hasOwnProperty(dateKey)) {
            result[index].days[dateKey] = 0;
          }
          result[index].days[dateKey] = value;
        }
      });
    });

    return result;
  };

  const handleExport = () => {
    if (reportData.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      // Define CSV headers - Metric column first, then all dates
      const headers = [
        'Metric',
        ...dateKeys.map(dateKey => formatDateLabel(dateKey))
      ];

      // Convert data to CSV rows
      const csvRows = [
        headers.join(','),
        ...reportData.map((row) => {
          const metricCell = `"${row.metric.replace(/"/g, '""')}"`;
          const dayCells = dateKeys.map(dateKey => {
            const value = row.days[dateKey];
            if (typeof value === 'number') {
              return value.toLocaleString('en-IN', { 
                minimumFractionDigits: 2, 
                maximumFractionDigits: 2 
              });
            }
            return value || '0';
          });
          return [metricCell, ...dayCells].join(',');
        })
      ];

      // Create CSV content
      const csvContent = csvRows.join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `MIS_Daily_Report_${timestamp}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`Exported MIS daily report to CSV`);
    } catch (error) {
      // console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6">
        {/* Header */}
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/reports')}
                  className="border-primary-300 hover:bg-primary-100 text-primary-700"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <div>
                  <CardTitle className="text-2xl text-gray-900">MIS Daily Report</CardTitle>
                  <p className="text-gray-600 text-sm mt-1">
                    Daily Summary Index Report
                  </p>
                </div>
              </div>
              {reportData.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  className="border-primary-300 hover:bg-primary-100 text-primary-700"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              )}
            </div>
          </CardHeader>
        </Card>

        {/* Date Range Selection */}
        <Card className="border-primary-200 shadow-sm">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary-600" />
              Date Range Selection
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="startDate" className="mb-2 block">Start Date *</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
              </div>
              <div>
                <Label htmlFor="endDate" className="mb-2 block">End Date *</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={loadReportData}
                  disabled={loading || !startDate || !endDate}
                  className="w-full bg-primary-600 hover:bg-primary-700 text-white"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Load Report'
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Report Table */}
        <Card className="border-primary-200 shadow-md">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                <span className="ml-3 text-gray-600">Loading report data...</span>
              </div>
            ) : reportData.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="sticky left-0 z-20 bg-gray-50 min-w-[300px] font-semibold border-r border-gray-200">
                        Metric
                      </TableHead>
                      {dateKeys.map((dateKey) => (
                        <TableHead 
                          key={dateKey} 
                          className="text-center min-w-[120px] font-semibold"
                        >
                          {formatDateLabel(dateKey)}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.map((row, index) => (
                      <TableRow key={index} className="hover:bg-gray-50">
                        <TableCell className="sticky left-0 z-10 bg-white font-medium min-w-[300px] border-r border-gray-200">
                          {row.metric}
                        </TableCell>
                        {dateKeys.map((dateKey) => (
                          <TableCell 
                            key={dateKey} 
                            className="text-center"
                          >
                            {typeof row.days[dateKey] === 'number' 
                              ? row.days[dateKey].toLocaleString('en-IN')
                              : row.days[dateKey] || '0'}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                <p>No data available. Please select a date range and load the report.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MISReportPage;

